import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Input, Row, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import FinanceInvoices from 'libs/api/finance-invoices';
import UIHelper from 'helpers/UIHelper';
import { invoiceAdapter } from './invoice.adapter';
import Helpers from '../../../../../helpers';
import { useNavigate } from 'react-router-dom';
import { getTotals } from './invoiceCalculations';
import moment from 'moment';

const financeInvoicesApi = new FinanceInvoices();

const integerFields = ['receipts', 'pieces', 'visitors'];

function taxed_amount(val, percent) {
  val = val.replace(',', '.');
  if (percent === 0) {
    return val;
  }
  percent = percent / 100 + 1;
  let with_taxes = parseFloat(val) * parseFloat(percent);
  return with_taxes.toString().match(/^-?\d+(?:\.\d{0,2})?/)
    ? with_taxes.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]
    : '';
}

// Day is outside company activity period (before opening or after closing).
// Empty opening/closing date means no limit on that side.
const isOutsideCompanyPeriod = (invoiceDate, company) => {
  if (!invoiceDate) return false;
  const date = moment(invoiceDate, 'YYYY-MM-DD');
  const { opening_date: openingDate, closing_date: closingDate } =
    company || {};

  if (openingDate && date.isBefore(moment(openingDate, 'YYYY-MM-DD'), 'day')) {
    return true;
  }
  if (closingDate && date.isAfter(moment(closingDate, 'YYYY-MM-DD'), 'day')) {
    return true;
  }
  return false;
};

const getVal = (value) => {
  return Math.trunc(value.replace ? value.replace(/[^\d.-]/g, '') : value);
};

const useEditInvoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { singleItem, onUpdate, updateInProcess, formErrors } = useApi({
    isMainPage: false,
    api: new FinanceInvoices(),
    singleResourceKey: 'invoices',
  });
  const [isWeekClosed, setIsWeekClosed] = useState(false);
  const [initialWeekClosed, setInitialWeekClosed] = useState(false);

  const [items, setItems] = useState([]);
  const [comment, setComment] = useState('');
  const [approveRejectInProcess, setApproveRejectInProcess] = useState(false);

  useEffect(() => {
    if (singleItem?.allData?.invoices) {
      const company = singleItem?.allData?.company;
      setItems(
        invoiceAdapter(singleItem?.allData?.invoices).map((i) => ({
          ...i,
          outOfCompanyPeriod: isOutsideCompanyPeriod(i.invoice_date, company),
        })),
      );
    }
  }, [singleItem]);

  useEffect(() => {
    const weekClosedFromBackend = singleItem?.allData?.invoices[0]?.week_closed;
    setInitialWeekClosed(weekClosedFromBackend);
    setIsWeekClosed(weekClosedFromBackend);
  }, [singleItem?.allData?.invoices]);

  const onChangeValuesV2 = useCallback((row) => {
    return (e) => {
      let {
        target: { value, name },
      } = e;
      setItems((s) =>
        s.map((i) => {
          if (i.id === row.id) {
            const needChangeField = {};
            const { vat = 0 } = row.company || {};

            switch (name) {
              case 'taxable': {
                const taxed = taxed_amount(value, vat);
                let atv = 0;
                if (taxed !== value) {
                  needChangeField['taxable_vat'] = taxed.replace
                    ? taxed.replace('.', ',')
                    : taxed;
                }
                if (taxed > 0) {
                  const receipts = i.receipts;
                  if (receipts > 0) {
                    atv = taxed / parseFloat(receipts);
                    atv = parseFloat(atv).toFixed(2);
                  }
                }
                needChangeField['atv'] = atv;
                break;
              }
              case 'receipts': {
                value = getVal(value);
                const rValue = Number(value);
                // ATV logic start
                if (!(Number(i.pieces) > 0 && value > Number(i.pieces))) {
                  needChangeField['errorFields'] = [];
                }
                if (rValue > 0) {
                  let taxed = i.taxable_vat.replace
                    ? i.taxable_vat.replace(',', '.')
                    : i.taxable_vat;
                  if (taxed > 0) {
                    taxed = parseFloat(taxed);
                    let atv = taxed / parseFloat(value);
                    atv = atv.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
                    atv = atv.replace('.', ',');
                    needChangeField['atv'] = atv;
                  } else {
                    needChangeField['atv'] = 0;
                  }
                  // UPT logic start

                  const pieces = i.pieces;
                  if (pieces > 0) {
                    let upt = pieces / Number(value);
                    upt = upt.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
                    upt = upt.replace('.', ',');
                    needChangeField['upt'] = upt;
                  } else {
                    needChangeField['upt'] = 0;
                  }
                  const visitors = i.visitors;
                  if (visitors > 0) {
                    let conv = Number(value / visitors);
                    conv = conv.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
                    conv = conv.replace('.', ',');
                    needChangeField['conversion_rate'] = conv;
                  } else {
                    needChangeField['conversion_rate'] = 0;
                  }
                } else {
                  needChangeField['atv'] = 0;
                  needChangeField['upt'] = 0;
                  needChangeField['conversion_rate'] = 0;
                }
                break;
              }
              case 'pieces': {
                value = getVal(value);
                if (Number(value) > 0) {
                  const rec = i.receipts;
                  if (rec <= value) {
                    needChangeField['errorFields'] = [];
                  }
                  if (rec > 0) {
                    let upt = Number(value) / Number(rec);
                    upt = upt.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
                    upt = upt.replace('.', ',');
                    needChangeField['upt'] = upt;
                  } else {
                    needChangeField['upt'] = 0;
                  }
                } else {
                  needChangeField['upt'] = 0;
                }
                break;
              }
              case 'visitors': {
                if (Number(value) > 0) {
                  const rec = i.receipts;
                  if (rec > 0) {
                    let conv = Number(rec) / Number(value);
                    conv = conv.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];
                    conv = conv.replace('.', ',');
                    needChangeField['conversion_rate'] = conv;
                  } else {
                    needChangeField['conversion_rate'] = 0;
                  }
                } else {
                  needChangeField['conversion_rate'] = 0;
                }
                value = getVal(value);
                break;
              }
              default:
                break;
            }

            return {
              ...i,
              ...needChangeField,
              [name]: value,
            };
          }
          return i;
        }),
      );
    };
  }, []);

  const onChangeCheckboxValues = useCallback((row) => {
    return (e) => {
      const {
        target: { checked, name },
      } = e;
      setItems((s) =>
        s.map((i) => {
          if (i.id === row.id) {
            return {
              ...i,
              [name]: checked,
              taxable: '0',
              taxable_vat: '0',
              receipts: '0',
              pieces: '0',
              visitors: '0',
              atv: '0',
              upt: '0',
              conversion_rate: '0',
            };
          }
          return i;
        }),
      );
    };
  }, []);

  const render = useCallback(
    (name) => {
      return (data, row) => {
        const today = new Date();
        const rowDate = new Date(row.invoice_date);
        const isFuture = rowDate > today;
        const isLocked = row.day_off || isFuture || row.outOfCompanyPeriod;
        const errorStyle = row.errorFields?.includes(name)
          ? { boxShadow: 'rgb(225, 5, 5) 0px 1px 0px' }
          : {};
        return (
          <Input
            readOnly={isLocked}
            disabled={
              isLocked || (name === 'taxable_vat' && row.company?.vat > 0)
            }
            onChange={onChangeValuesV2(row)}
            name={name}
            onKeyPress={UIHelper.typeNumber}
            value={data}
            style={errorStyle}
          />
        );
      };
    },
    [onChangeValuesV2],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'invoice_date',
        // fixed: 'left',
        minWidth: 120,
        width: 120,
        sorter: () => {},
        render: (row) => UIHelper.formatedDate(row, 'dddd, DD MMMM YYYY'),
      },
      {
        title: t('Netto (€)'),
        dataIndex: 'taxable',
        sorter: () => {},
        width: 120,
        render: render('taxable'),
      },
      {
        title: t('Lordo (€) %'),
        dataIndex: 'taxable_vat',
        sorter: () => {},
        width: 120,
        render: render('taxable_vat'),
      },
      {
        // Checks
        title: t('SCONTRINI'),
        dataIndex: 'receipts',
        sorter: () => {},
        width: 120,
        render: render('receipts'),
      },
      {
        // Products
        title: t('PEZZI'),
        dataIndex: 'pieces',
        sorter: () => {},
        width: 120,
        render: render('pieces'),
      },
      {
        title: t('VISITATORI'),
        dataIndex: 'visitors',
        sorter: () => {},
        width: 120,
        render: render('visitors'),
      },
      {
        title: t('ATV'),
        dataIndex: 'atv',
        width: 80,
        sorter: () => {},
      },
      {
        title: t('UPT'),
        dataIndex: 'upt',
        width: 80,
        sorter: () => {},
      },
      {
        title: t('Conversion Rate'),
        dataIndex: 'conversion_rate',
        width: 140,
        sorter: () => {},
      },

      {
        title: 'GIORNO DI CHIUSURA',
        dataIndex: 'day_off',
        align: 'right',
        width: 100,
        // fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              name='day_off'
              checked={data}
              disabled={row.outOfCompanyPeriod}
              onChange={onChangeCheckboxValues(row)}
            />
          </Row>
        ),
      },
    ];
  }, [t, render, onChangeCheckboxValues]);

  const tableSummary = (pageData) => {
    const {
      taxable,
      taxable_vat,
      receipts,
      pieces,
      visitors,
      atv,
      upt,
      conversion_rate,
    } = getTotals(pageData);
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ fontWeight: 'bold' }}>
          <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(taxable)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(taxable_vat)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(receipts)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(pieces)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(visitors)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(atv)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(upt)}
          </Table.Summary.Cell>
          <Table.Summary.Cell>
            {Helpers.numberWithCommas(conversion_rate)}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const handleUpdate = () => {
    const data = {
      invoice_date: [],
      week_day: [],
      conversion: [],
      upt: [],
      atv: [],
      visitors: [],
      pieces: [],
      receipts: [],
      taxable_vat: [],
      taxable: [],
      dayoff: [],
      // week_closed: [],
    };

    const checksKey = 'receipts';
    const productsKey = 'pieces';
    let hasError = false;
    const failedItems = [];

    items.forEach((i) => {
      let errorFields = [];

      if (i[checksKey] > i[productsKey]) {
        errorFields.push(checksKey, productsKey);
      }


      const netto = parseFloat(i.taxable.toString().replace(',', '.')) || 0;
      const lordo = parseFloat(i.taxable_vat.toString().replace(',', '.')) || 0;
      if (lordo < netto) {
        errorFields.push('taxable', 'taxable_vat');
      }

      if (errorFields.length > 0) {
        failedItems.push({
          ...i,
          errorFields,
        });
        hasError = true;
      }
    });

    if (hasError) {
      setItems((s) => [
        ...s.map((i) => failedItems.find((f) => f.id === i.id) || i),
      ]);

      const hasTaxableError = failedItems.some(
        (item) =>
          item.errorFields.includes('taxable') ||
          item.errorFields.includes('taxable_vat'),
      );
      const hasReceiptsPiecesError = failedItems.some(
        (item) =>
          item.errorFields.includes(checksKey) &&
          item.errorFields.includes(productsKey),
      );

      let errorMessage = '';
      if (hasTaxableError && hasReceiptsPiecesError) {
        errorMessage =
          'Il campo "Lordo" deve essere maggiore del campo "Netto"; Il campo "Pezzi" deve essere uguale o maggiore del campo "Scontrini".';
      } else if (hasTaxableError) {
        errorMessage =
          'Il campo "Lordo" deve essere maggiore del campo "Netto".';
      } else if (hasReceiptsPiecesError) {
        errorMessage =
          'Il campo "Pezzi" deve essere uguale o maggiore del campo "Scontrini".';
      }

      UIHelper.openNotificationWithIcon('error', errorMessage);
      return;
    }
    // singleItem.allData?.invoices?.forEach((row, index) => {
    items?.forEach((row, index) => {
      Object.keys(data).forEach((key) => {
        if (key === 'dayoff') {
          if (row['day_off'] === true) {
            data[key] = [...(data[key] || []), index.toString()];
          }
          return;
        }
        if (key === 'conversion') {
          data[key][index] = row.conversion_rate.toString();
          return;
        }
        if (integerFields.includes(key)) {
        }
        data[key][index] = row[key].toString();
      });
    });
    onUpdate({ ...data, week_closed: isWeekClosed });
  };

  const invoiceId = items?.[0]?.id;
  const companyId = items?.[0]?.company?.id;

  const handleAcceptOrRefuse = async (values) => {
    if (!items?.[0]?.id || !items?.[0]?.company?.id) return;

    const invoiceId = items[0].id;
    const companyId = items[0].company.id;

    if (values.status === 'Rifiutato' && !values.description) {
      UIHelper.openNotificationWithIcon(
        'error',
        t('Commento obbligatorio, inserire notifica per il richiedente'),
      );
      return;
    }

    setApproveRejectInProcess(true);
    try {
      let status = 'confirm';
      if (values.status === 'Rifiutato') {
        status = 'reject';
      }

      await financeInvoicesApi.approveInvoice(
        invoiceId,
        companyId,
        values.description || '',
        status,
      );

      navigate(-1);

      setComment('');
    } catch (error) {
      UIHelper.openNotificationWithIcon(
        'error',
        t('Errore durante la richiesta'),
      );
    } finally {
      setApproveRejectInProcess(false);
    }
  };
  useEffect(() => {
    if (formErrors) {
      const values = Object.values(formErrors);
      if (values.length > 0) {
        const errorMessage = values.flat().join('\n');
        UIHelper.openNotificationWithIcon('error', errorMessage);
      }
    }
  }, [formErrors]);

  return {
    columns,
    // data: Array.isArray(singleItem?.allData?.invoices) ? singleItem?.allData?.invoices : [],
    data: Array.isArray(items) ? items : [],
    tableSummary,
    onUpdate: handleUpdate,
    updateInProcess,
    comment,
    setComment,
    handleAcceptOrRefuse,
    approveRejectInProcess,
    singleItem,
    isWeekClosed,
    setIsWeekClosed,
    initialWeekClosed, setInitialWeekClosed
  };
};

export default useEditInvoices;
